import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import apiClient from '../utils/apiClient';
import { sendBulkEmails } from '../utils/APIRoutes';
import { useServerRole } from '../utils/permissions';

// UserRequests.js has linked to `/emails/:userId` since it was
// written, but this screen never existed — the link went nowhere.
// This is the composer for POST /send-bulk-emails
// (requireAdmin(['send_emails'])) — the most sensitive endpoint in
// the app, since it emails every registered user. Kept intentionally
// plain: a subject, a message, a confirm step, nothing fancier.
const ContactEmails = () => {
  const { userId } = useParams();
  const { isAdmin, hasPermission, loading: roleLoading } = useServerRole();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canSend = isAdmin && hasPermission('send_emails');

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setResult(null);

    try {
      const { data } = await apiClient.post(sendBulkEmails, {
        title: title.trim(),
        message: message.trim(),
      });
      setResult({ type: 'success', text: data.msg, details: data.results });
      setTitle('');
      setMessage('');
    } catch (error) {
      setResult({
        type: 'error',
        text: error.response?.data?.msg || 'Error sending emails',
      });
    } finally {
      setSending(false);
    }
  };

  if (roleLoading) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>Loading...</p>;
  }

  if (!canSend) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Link to={`/userrequests/${userId}`}>
          <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
        </Link>
        <h2>Access Denied</h2>
        <p>You don't have permission to send bulk emails.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Link to={`/userrequests/${userId}`}>
        <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
      </Link>

      <h1 style={{ fontSize: '20px', marginBottom: '6px' }}>Send Email to All Users</h1>
      <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '20px' }}>
        This sends to every registered user's email address. There's no undo.
      </p>

      {result && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px',
            color: 'white',
            backgroundColor: result.type === 'error' ? '#dc2626' : '#059669',
          }}
        >
          {result.text}
          {result.details && (
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {result.details.success} sent, {result.details.failed} failed of {result.details.total}
            </div>
          )}
        </div>
      )}

      <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Subject</label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={150}
        placeholder="Subject line"
        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '14px' }}
      />

      <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Message</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={5000}
        rows={8}
        placeholder="Message body"
        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '16px' }}
      />

      {!confirmOpen ? (
        <button
          type="button"
          disabled={sending || !title.trim() || !message.trim()}
          onClick={() => setConfirmOpen(true)}
          style={{
            padding: '10px 18px',
            borderRadius: '6px',
            border: 'none',
            background: !title.trim() || !message.trim() ? '#93c5fd' : '#2563eb',
            color: 'white',
            cursor: !title.trim() || !message.trim() ? 'default' : 'pointer',
          }}
        >
          Review & Send
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '13px' }}>Send to every user — are you sure?</span>
          <button
            type="button"
            disabled={sending}
            onClick={handleSend}
            style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer' }}
          >
            {sending ? 'Sending...' : 'Yes, send'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactEmails;
